import { useEffect, useState } from "react";
import { Spinner } from "../../components/Spinner/Spinner";
import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { listAdminTenants, listAdminMembers, getAdminAnalytics } from "../../lib/api/admin";
import { ApiError } from "../../lib/apiClient";
import { useAuth } from "../../lib/auth/AuthContext";
import { CreateTenantForm } from "../owner/CreateTenantForm";
import { TenantTabs } from "../owner/TenantTabs";
import { TenantWorkspace } from "../owner/TenantWorkspace";
import { TenantGovernance } from "./TenantGovernance";
import type { AdminTenant } from "../../types";

type TenantsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; tenants: AdminTenant[] };

/** Administrator entry point: every business, with the ability to launch a new
 * one and inspect any business's member list. Read-write, unlike the
 * owner workspace, which never exposes a create-business control. */
export function AdminWorkspace() {
  const { accessToken, invalidateSession } = useAuth();
  const [tenantsState, setTenantsState] = useState<TenantsState>({ status: "loading" });
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [invitedTenantId, setInvitedTenantId] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    listAdminTenants(accessToken)
      .then(({ tenants }) => {
        if (cancelled) return;
        setTenantsState({ status: "ready", tenants });
        setSelectedTenantId((current) => current ?? tenants[0]?.tenantId ?? null);
        setAddingNew(tenants.length === 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          invalidateSession("Your session expired. Sign in again to continue.");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setTenantsState({
            status: "error",
            message: "Access denied. If your access changed recently, sign out and sign in again to refresh it.",
          });
          return;
        }
        setTenantsState({
          status: "error",
          message: err instanceof Error ? err.message : "Couldn't load businesses.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, invalidateSession]);

  function handleUnauthorized() {
    invalidateSession("Your session expired. Sign in again to continue.");
  }

  function handleCreated(tenant: AdminTenant, ownerInvitationSent: boolean) {
    setTenantsState((prev) => ({
      status: "ready",
      tenants: prev.status === "ready" ? [...prev.tenants, tenant] : [tenant],
    }));
    setSelectedTenantId(tenant.tenantId);
    setAddingNew(false);
    setInvitedTenantId(ownerInvitationSent ? tenant.tenantId : null);
  }

  /** A successful status change returns the full updated AdminTenant; splice it back into the
   * top-level list so the tenant switcher pill and workspace both reflect it without a reload. */
  function handleTenantUpdated(updated: AdminTenant) {
    setTenantsState((prev) =>
      prev.status === "ready"
        ? { status: "ready", tenants: prev.tenants.map((t) => (t.tenantId === updated.tenantId ? updated : t)) }
        : prev,
    );
  }

  return (
    <>
      {tenantsState.status === "loading" && <Spinner label="Loading businesses" />}
      {tenantsState.status === "error" && <InlineNotice tone="error">{tenantsState.message}</InlineNotice>}
      {tenantsState.status === "ready" && accessToken && (
        <>
          {tenantsState.tenants.length > 0 && (
            <TenantTabs
              tenants={tenantsState.tenants}
              selectedTenantId={selectedTenantId ?? ""}
              addingNew={addingNew}
              ariaLabel="All businesses"
              onSelect={(id) => {
                setSelectedTenantId(id);
                setAddingNew(false);
              }}
              onAddNew={() => setAddingNew(true)}
            />
          )}
          {addingNew && (
            <CreateTenantForm
              token={accessToken}
              onCreated={handleCreated}
              onCancel={tenantsState.tenants.length > 0 ? () => setAddingNew(false) : undefined}
            />
          )}
          {!addingNew &&
            selectedTenantId &&
            (() => {
              const tenant = tenantsState.tenants.find((t) => t.tenantId === selectedTenantId);
              if (!tenant) return null;
              const ownerNotice =
                tenant.ownerEmails.length === 0
                  ? "No owner assigned yet."
                  : `${tenant.ownerEmails.length > 1 ? "Owners" : "Owner"}: ${tenant.ownerEmails.join(", ")}`;
              return (
                <TenantWorkspace
                  tenant={tenant}
                  token={accessToken}
                  fetchMembers={listAdminMembers}
                  fetchAnalytics={getAdminAnalytics}
                  onUnauthorized={handleUnauthorized}
                  canEditBrand={false}
                  headerNotice={
                    <>
                      {invitedTenantId === tenant.tenantId && (
                        <InlineNotice tone="info" onDismiss={() => setInvitedTenantId(null)}>
                          Business launched. We sent {tenant.ownerEmails[0]} an email invitation to set up
                          their owner account.
                        </InlineNotice>
                      )}
                      <InlineNotice tone="info">{ownerNotice}</InlineNotice>
                      <TenantGovernance
                        tenant={tenant}
                        token={accessToken}
                        onUnauthorized={handleUnauthorized}
                        onTenantUpdated={handleTenantUpdated}
                      />
                    </>
                  }
                />
              );
            })()}
        </>
      )}
    </>
  );
}
